import React from 'react';
import { Checkbox, Divider, Input, Select, Typography, Empty, Spin, Row, Col, Space, Slider } from 'antd';
import MetadataPopover from './MetadataPopover';
import FormField from './FormField';
import FileUploader from '../../uploader/FileUploader.component';
import { ANSWER_FIELD_TYPES } from '../utils/constants';
import { isAnswerEnabled } from '../utils/previewHelpers';

const { Title, Text } = Typography;

/**
 * Red asterisk shown next to labels of required questions
 */
const RequiredMark = ({ question }) =>
  question?.required ? <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span> : null;

/**
 * Render answer label with metadata popover
 */
export const renderAnswerLabel = (answer, labelText) => {
  const terminology = answer.terminology;
  const hasTerminology = terminology && (terminology.code || terminology.system);

  if (!hasTerminology) {
    return <span>{labelText}</span>;
  }

  return (
    <MetadataPopover terminology={terminology}>
      <span style={{ cursor: 'pointer' }}>
        {labelText}
      </span>
    </MetadataPopover>
  );
};

/**
 * Render label with terminology metadata (for checkbox terminology)
 */
export const renderLabelWithTerminology = (terminology, labelText) => {
  const hasTerminology = terminology && (terminology.code || terminology.system);

  if (!hasTerminology) {
    return <span>{labelText}</span>;
  }

  return (
    <MetadataPopover terminology={terminology}>
      <span style={{ cursor: 'pointer' }}>
        {labelText}
      </span>
    </MetadataPopover>
  );
};

/**
 * Recursive children renderer — works for any answer type at any nesting depth.
 * Standard children render as checkboxes; component children render as FormField.
 * Each child with its own children nests further when activated.
 * Each answer can have its own childrenLayout and childrenColumns settings.
 */
export const createChildrenRenderer = (formValues, handleCheckboxChange, readonly) => {
  // parentAnswer contains the layout config for its children
  // childKeyPrefix ensures unique child IDs when multiple answers have similar children
  const renderChildren = (children, storeKey, parentAnswer = {}, childKeyPrefix = '') => {
    if (!children?.length) return null;
    
    const layout = parentAnswer.childrenLayout || 'vertical';
    const columns = parentAnswer.childrenColumns;
    const isHorizontal = layout === 'horizontal';
    const useGrid = isHorizontal && columns && typeof columns === 'number' && columns > 0;

    return (
      <div style={{ 
        paddingLeft: 24, 
        marginTop: 4,
        ...(useGrid ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '8px 16px'
        } : {
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          flexWrap: isHorizontal ? 'wrap' : 'nowrap',
          gap: isHorizontal ? '8px 16px' : '4px'
        })
      }}>
        {children.map((child, ci) => {
          // Child ID: use explicit id/concept/conceptCode, or generate unique fallback with prefix
          const childId = child.id || child.concept || child.conceptCode || `${childKeyPrefix}_child_${ci}`;

          // Component child → field widget
          if (child.componentType) {
            const ft = ANSWER_FIELD_TYPES.find(f => f.value === child.componentType);
            return (
              <div key={childId} style={{ padding: '2px 0' }}>
                <FormField
                  field={{
                    id: `${storeKey}_${childId}`,
                    type: child.componentType,
                    label: child.label || ft?.label || child.componentType,
                    ...(child.options?.length > 0 && {
                      options: child.options.map((opt, oi) => ({
                        value: opt.label || `option_${oi}`,
                        label: opt.label || `Option ${oi + 1}`,
                      })),
                    }),
                  }}
                  value={formValues[storeKey]?.[childId]}
                  onChange={(_, val) => handleCheckboxChange(storeKey, childId, val)}
                  disabled={readonly}
                />
                {child.children?.length > 0 && renderChildren(child.children, `${storeKey}_${childId}`, child, childId)}
              </div>
            );
          }

          // Standard child → checkbox with recursive nesting
          return (
            <div key={childId} style={{ padding: '2px 0' }}>
              <Checkbox
                checked={formValues[storeKey]?.[childId] || false}
                onChange={(e) => handleCheckboxChange(storeKey, childId, e.target.checked)}
                disabled={readonly}
              >
                {renderAnswerLabel(child, child.label)}
              </Checkbox>
              {/* Recursively render this child's children with the child's own layout settings */}
              {child.children?.length > 0 && formValues[storeKey]?.[childId] &&
                renderChildren(child.children, storeKey, child, childId)
              }
            </div>
          );
        })}
      </div>
    );
  };
  return renderChildren;
};

/**
 * Create question renderer function
 */
export const createQuestionRenderer = (formValues, handleInputChange, handleCheckboxChange, readonly, compact, isQuestionEnabled, externalForms, setExternalForms, onLoadExternalForm, renderExternalForm, schema) => {
  // Create renderChildren - layout config comes from each answer's own settings
  const renderChildren = createChildrenRenderer(formValues, handleCheckboxChange, readonly);

  const renderQuestion = (question) => {
    // Check if question should be displayed based on conditions (universal dependency)
    if (!isQuestionEnabled(question, formValues, schema)) {
      return null;
    }

    // showInPreview flag only controls label visibility, not the entire question
    const showLabel = question.showInPreview !== false;
    const effectiveReadonly = readonly || !!question.readOnly || !!question.notApplicable;
    const saved = formValues[question.id];
    const naEmpty =
      !!question.notApplicable &&
      (saved == null || saved === '' || (typeof saved === 'object' && !Array.isArray(saved) && Object.keys(saved).length === 0));

    // Inactive / N/A questions with no saved answer: show disabled N/A (keep schema IDs).
    if (naEmpty) {
      return (
        <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
          {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
          <Input
            value="N/A"
            disabled
            style={{ maxWidth: 200, marginTop: 8, marginLeft: compact ? 0 : 12, display: 'block' }}
          />
        </div>
      );
    }

    // Handle Rich Text type
    if (question.type === 'richtext') {
      return (
        <div 
          key={question.id}
          style={{ 
            marginBottom: 20,
            padding: '16px',
            backgroundColor: 'transparent',
            borderRadius: '4px'
          }}
        >
          {showLabel && question.label && (
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {question.label}
            </Text>
          )}
          <div 
            dangerouslySetInnerHTML={{ __html: question.content || '' }}
            style={{ 
              lineHeight: '1.6'
            }}
          />
        </div>
      );
    }

    const rendering = question.questionOptions?.rendering;
    const layout = question.questionOptions?.layout || 'vertical';
    const isHorizontal = layout === 'horizontal';

    switch (rendering) {
      case 'checkbox':
        return renderCheckboxQuestion(question, formValues, handleCheckboxChange, effectiveReadonly, compact, isHorizontal, renderChildren, showLabel);
      
      case 'radio':
        return renderRadioQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, isHorizontal, renderChildren, showLabel);
      
      case 'select':
        return renderSelectQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'checkbox-with-select':
        return renderCheckboxWithSelectQuestion(question, formValues, handleCheckboxChange, effectiveReadonly, compact, showLabel);
      
      case 'date':
        return renderDateQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'text':
        return renderTextQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'textarea':
        return renderTextareaQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'number':
        return renderNumberQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'scale':
        return renderScaleQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'file':
        return renderFileQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, showLabel);
      
      case 'group':
        return renderGroupQuestion(question, renderQuestion, compact);
      
      case 'embedded-section':
        return renderEmbeddedSectionQuestion(question, formValues, renderQuestion, compact);
      
      case 'external-form':
        return renderExternalFormQuestion(question, formValues, handleInputChange, effectiveReadonly, compact, externalForms, setExternalForms, onLoadExternalForm, renderExternalForm);
      
      default:
        if (question.questions) {
          return (
            <div key={question.id} style={{ marginBottom: compact ? 16 : 24 }}>
              {question.label && <Title level={5}>{question.label}</Title>}
              <div style={{ marginLeft: compact ? 8 : 16, marginTop: 8 }}>
                {question.questions.map(renderQuestion)}
              </div>
            </div>
          );
        }
        return renderTextQuestion(question, formValues, handleInputChange, effectiveReadonly, compact);
    }
  };

  return renderQuestion;
};

// Individual question type renderers
const renderCheckboxQuestion = (question, formValues, handleCheckboxChange, readonly, compact, isHorizontal, renderChildren, showLabel = true) => {
  const otherValue = formValues[question.id]?.['__other__'];
  const isOtherChecked = typeof otherValue === 'object' ? otherValue?.checked : false;
  const otherTextValue = typeof otherValue === 'object' ? otherValue?.otherValue : '';
  const columns = question.questionOptions?.columns;
  const useGrid = isHorizontal && columns && typeof columns === 'number' && columns > 0;
  
  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{
        marginTop: 8,
        ...(useGrid ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '16px'
        } : {
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          flexWrap: isHorizontal ? 'wrap' : 'nowrap',
          gap: isHorizontal ? '16px' : '8px'
        })
      }}>
        {question.questionOptions?.answers?.map((answer, idx) => {
          // Visual elements
          if (answer.type === 'separator') return <Divider key={`sep_${idx}`} style={{ margin: '4px 0' }} />;
          if (answer.type === 'linebreak') return <div key={`lb_${idx}`} style={{ height: 12 }} />;

          const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
          
          // Check conditional display
          if (!isAnswerEnabled(answer, question, question.questionOptions?.answers, 'checkbox', formValues)) return null;
          
          // Render field component answer
          if (answer.componentType) {
            const fieldType = ANSWER_FIELD_TYPES.find(ft => ft.value === answer.componentType);
            return (
              <div key={answerId} style={{ marginBottom: 8, width: '100%' }}>
                <FormField
                  field={{
                    id: `${question.id}_${answerId}`,
                    type: answer.componentType,
                    label: answer.label || fieldType?.label || answer.componentType,
                    ...(answer.options?.length > 0 && {
                      options: answer.options.map((opt, oi) => ({
                        value: opt.label || `option_${oi}`,
                        label: opt.label || `Option ${oi + 1}`,
                      })),
                    }),
                  }}
                  value={formValues[question.id]?.[answerId]}
                  onChange={(fieldId, val) => handleCheckboxChange(question.id, answerId, val)}
                  disabled={readonly}
                />
              </div>
            );
          }
          
          // Render standard checkbox with recursive children
          return (
            <div key={answerId} style={{ padding: '4px 0' }}>
              <Checkbox
                checked={formValues[question.id]?.[answerId] || false}
                onChange={(e) => handleCheckboxChange(question.id, answerId, e.target.checked)}
                disabled={readonly}
              >
                {renderAnswerLabel(answer, answer.label)}
              </Checkbox>
              {answer.children?.length > 0 && formValues[question.id]?.[answerId] &&
                renderChildren(answer.children, question.id, answer, answerId)
              }
            </div>
          );
        })}
        {question.questionOptions?.allowOther && (
          <div style={{ 
            padding: '4px 0', 
            width: '100%',
            ...(useGrid && { gridColumn: `1 / -1` })
          }}>
            <Checkbox
              checked={isOtherChecked || false}
              onChange={(e) => {
                const newValue = {
                  checked: e.target.checked,
                  otherValue: e.target.checked ? (otherTextValue || '') : ''
                };
                handleCheckboxChange(question.id, '__other__', newValue);
              }}
              disabled={readonly}
            >
              {question.questionOptions?.otherLabel || 'Other'}
            </Checkbox>
            {isOtherChecked && (
              <Input
                placeholder="Please specify"
                value={otherTextValue || ''}
                onChange={(e) => {
                  handleCheckboxChange(question.id, '__other__', {
                    checked: true,
                    otherValue: e.target.value
                  });
                }}
                disabled={readonly}
                style={{ marginTop: 8, marginLeft: 24, maxWidth: 400 }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const renderRadioQuestion = (question, formValues, handleInputChange, readonly, compact, isHorizontal, renderChildren, showLabel = true) => {
  const radioValue = formValues[question.id];
  const isRadioOtherSelected = typeof radioValue === 'object' && radioValue?.value === '__other__';
  const radioOtherText = typeof radioValue === 'object' ? radioValue?.otherValue : '';
  const selectedRadioId = typeof radioValue === 'object' ? radioValue?.value : radioValue;
  const columns = question.questionOptions?.columns;
  const useGrid = isHorizontal && columns && typeof columns === 'number' && columns > 0;
  
  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{
        marginTop: 8,
        ...(useGrid ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '16px'
        } : {
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          flexWrap: isHorizontal ? 'wrap' : 'nowrap',
          gap: isHorizontal ? '16px' : '8px'
        })
      }}>
        {question.questionOptions?.answers?.map((answer, idx) => {
          // Visual elements
          if (answer.type === 'separator') return <Divider key={`sep_${idx}`} style={{ margin: '4px 0' }} />;
          if (answer.type === 'linebreak') return <div key={`lb_${idx}`} style={{ height: 12 }} />;

          const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
          
          // Check conditional display
          if (!isAnswerEnabled(answer, question, question.questionOptions?.answers, 'radio', formValues)) return null;
          
          // Render field component answer
          if (answer.componentType) {
            const fieldType = ANSWER_FIELD_TYPES.find(ft => ft.value === answer.componentType);
            return (
              <div key={answerId} style={{ marginBottom: 8, width: '100%' }}>
                <FormField
                  field={{
                    id: `${question.id}_${answerId}`,
                    type: answer.componentType,
                    label: answer.label || fieldType?.label || answer.componentType,
                    ...(answer.options?.length > 0 && {
                      options: answer.options.map((opt, oi) => ({
                        value: opt.label || `option_${oi}`,
                        label: opt.label || `Option ${oi + 1}`,
                      })),
                    }),
                  }}
                  value={formValues[question.id]?.[answerId]}
                  onChange={(fieldId, val) => handleInputChange(`${question.id}.${answerId}`, val)}
                  disabled={readonly}
                />
              </div>
            );
          }
          
          const isThisSelected = !isRadioOtherSelected && selectedRadioId === answerId;

          // Render standard radio button with recursive children
          return (
            <div key={answerId} style={{ padding: '4px 0' }}>
              <input
                type="radio"
                name={question.id}
                checked={isThisSelected}
                onChange={() => handleInputChange(question.id, answerId)}
                disabled={readonly}
                style={{ marginRight: 8 }}
              />
              <label>{renderAnswerLabel(answer, answer.label)}</label>
              {answer.children?.length > 0 && isThisSelected &&
                renderChildren(answer.children, `${question.id}_children`, answer, answerId)
              }
            </div>
          );
        })}
        {question.questionOptions?.allowOther && (
          <div style={{ 
            padding: '4px 0', 
            width: '100%',
            ...(useGrid && { gridColumn: `1 / -1` })
          }}>
            <input
              type="radio"
              name={question.id}
              checked={isRadioOtherSelected}
              onChange={() => handleInputChange(question.id, { value: '__other__', otherValue: '' })}
              disabled={readonly}
              style={{ marginRight: 8 }}
            />
            <label>{question.questionOptions?.otherLabel || 'Other'}</label>
            {isRadioOtherSelected && (
              <Input
                placeholder="Please specify"
                value={radioOtherText || ''}
                onChange={(e) => handleInputChange(question.id, { value: '__other__', otherValue: e.target.value })}
                disabled={readonly}
                style={{ marginTop: 8, marginLeft: 24, maxWidth: 400 }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const renderSelectQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => {
  const selectValue = formValues[question.id];
  const isSelectOtherSelected = typeof selectValue === 'object' && selectValue?.value === '__other__';
  const selectOtherText = typeof selectValue === 'object' ? selectValue?.otherValue : '';
  const selectDisplayValue = typeof selectValue === 'object' ? selectValue?.value : selectValue;
  
  // Filter out visual elements for select options
  const selectAnswers = (question.questionOptions?.answers || []).filter(a => a.type !== 'separator' && a.type !== 'linebreak');
  
  const selectedAnswer = selectAnswers.find(
    (answer, idx) => {
      const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
      return answerId === selectDisplayValue;
    }
  );
  const selectedTerminology = selectedAnswer?.terminology;
  const hasSelectedTerminology = selectedTerminology && (selectedTerminology.code || selectedTerminology.system);

  // Build grouped options: parent answers with children become OptGroups
  const renderSelectOptions = () => {
    return selectAnswers.map((answer, idx) => {
      const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
      if (answer.children?.length > 0) {
        return (
          <Select.OptGroup key={answerId} label={answer.label}>
            {answer.children.map((child, ci) => {
              const childId = child.concept || child.conceptCode || `${answerId}_child_${ci}`;
              return (
                <Select.Option key={childId} value={childId}>
                  {`${answer.label} ${child.label}`}
                </Select.Option>
              );
            })}
          </Select.OptGroup>
        );
      }
      return (
        <Select.Option key={answerId} value={answerId}>
          {answer.label}
        </Select.Option>
      );
    });
  };

  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginLeft: compact ? 0 : 12 }}>
        <Select
          value={isSelectOtherSelected ? '__other__' : selectDisplayValue}
          onChange={(value) => {
            if (value === null || value === undefined) {
              handleInputChange(question.id, null);
            } else if (value === '__other__') {
              handleInputChange(question.id, { value: '__other__', otherValue: '' });
            } else {
              handleInputChange(question.id, value);
            }
          }}
          style={{ width: '100%', maxWidth: 400 }}
          placeholder={`Select ${question.label}`}
          disabled={readonly}
          allowClear
        >
          {renderSelectOptions()}
          {question.questionOptions?.allowOther && (
            <Select.Option value="__other__">
              {question.questionOptions?.otherLabel || 'Other'}
            </Select.Option>
          )}
        </Select>
        {isSelectOtherSelected && (
          <Input
            placeholder="Please specify"
            value={selectOtherText || ''}
            onChange={(e) => handleInputChange(question.id, { value: '__other__', otherValue: e.target.value })}
            disabled={readonly}
            style={{ width: '100%', maxWidth: 400 }}
          />
        )}
        {hasSelectedTerminology && (
          <MetadataPopover terminology={selectedTerminology}>
            <span style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}>
              View Metadata
            </span>
          </MetadataPopover>
        )}
      </div>
    </div>
  );
};

const renderCheckboxWithSelectQuestion = (question, formValues, handleCheckboxChange, readonly, compact, showLabel = true) => {
  const checkboxSelectLayout = question.questionOptions?.layout || 'vertical';
  const isCheckboxSelectHorizontal = checkboxSelectLayout === 'horizontal';
  const columns = question.questionOptions?.columns;
  const useGrid = isCheckboxSelectHorizontal && columns && typeof columns === 'number' && columns > 0;
  
  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{
        marginTop: 8,
        ...(useGrid ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '16px'
        } : {
          display: 'flex',
          flexDirection: isCheckboxSelectHorizontal ? 'row' : 'column',
          flexWrap: isCheckboxSelectHorizontal ? 'wrap' : 'nowrap',
          gap: isCheckboxSelectHorizontal ? '16px' : '8px'
        })
      }}>
        {question.questionOptions?.answers?.map((answer, idx) => {
          const answerId = answer.checkboxConceptCode || answer.checkboxTerminology?.id || `checkbox-select_${idx}`;
          const checkboxValue = formValues[question.id]?.[answerId]?.checked || false;
          const selectValue = formValues[question.id]?.[answerId]?.selectValue;
          const isOtherSelected = typeof selectValue === 'object' && selectValue?.value === '__other__';
          const otherTextValue = typeof selectValue === 'object' ? selectValue?.otherValue : '';
          
          const checkboxLabel = answer.checkboxLabel || answer.checkboxTerminology?.display || 'Checkbox Label';
          const checkboxLabelElement = renderLabelWithTerminology(answer.checkboxTerminology, checkboxLabel);
          
          return (
            <div key={answerId} style={{ padding: '8px 0' }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Checkbox
                  checked={checkboxValue}
                  onChange={(e) => {
                    handleCheckboxChange(question.id, answerId, {
                      checked: e.target.checked,
                      selectValue: e.target.checked ? (selectValue || null) : null
                    });
                  }}
                  disabled={readonly}
                >
                  {checkboxLabelElement}
                </Checkbox>
                {checkboxValue && (
                  <div style={{ marginLeft: 24 }}>
                    <Select
                      value={isOtherSelected ? '__other__' : selectValue}
                      onChange={(value) => {
                        if (value === null || value === undefined) {
                          handleCheckboxChange(question.id, answerId, {
                            checked: true,
                            selectValue: null
                          });
                        } else if (value === '__other__') {
                          handleCheckboxChange(question.id, answerId, {
                            checked: true,
                            selectValue: { value: '__other__', otherValue: '' }
                          });
                        } else {
                          handleCheckboxChange(question.id, answerId, {
                            checked: true,
                            selectValue: value
                          });
                        }
                      }}
                      style={{ width: '100%', maxWidth: 400 }}
                      placeholder={question.questionOptions?.selectPlaceholder || 'Please specify'}
                      disabled={readonly}
                      allowClear
                    >
                      {answer.selectOptions?.map((option, optIdx) => (
                        <Select.Option key={optIdx} value={option.label || `option_${optIdx}`}>
                          {option.label}
                        </Select.Option>
                      ))}
                      {question.questionOptions?.allowOther && (
                        <Select.Option value="__other__">
                          {question.questionOptions?.otherLabel || 'Other'}
                        </Select.Option>
                      )}
                    </Select>
                    {isOtherSelected && (
                      <Input
                        placeholder="Please specify"
                        value={otherTextValue || ''}
                        onChange={(e) => {
                          handleCheckboxChange(question.id, answerId, {
                            checked: true,
                            selectValue: { value: '__other__', otherValue: e.target.value }
                          });
                        }}
                        disabled={readonly}
                        style={{ marginTop: 8, width: '100%', maxWidth: 400 }}
                      />
                    )}
                  </div>
                )}
              </Space>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const renderDateQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => (
  <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
    {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
    <Input
      type="date"
      value={formValues[question.id] || ''}
      onChange={(e) => handleInputChange(question.id, e.target.value)}
      style={{ maxWidth: 200, marginTop: 8, marginLeft: compact ? 0 : 12, display: 'block' }}
      disabled={readonly}
    />
  </div>
);

const renderTextQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => (
  <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
    {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
    <Input
      value={formValues[question.id] || ''}
      onChange={(e) => handleInputChange(question.id, e.target.value)}
      style={{ maxWidth: 400, marginTop: 8, marginLeft: compact ? 0 : 12, display: 'block' }}
      disabled={readonly}
    />
  </div>
);

const renderTextareaQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => (
  <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
    {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
    <Input.TextArea
      value={formValues[question.id] || ''}
      onChange={(e) => handleInputChange(question.id, e.target.value)}
      style={{ marginTop: 8, marginLeft: compact ? 0 : 12 }}
      rows={compact ? 3 : 4}
      disabled={readonly}
    />
  </div>
);

const renderNumberQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => (
  <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
    {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
    <Input
      type="number"
      value={formValues[question.id] || ''}
      onChange={(e) => handleInputChange(question.id, e.target.value)}
      style={{ maxWidth: 150, marginTop: 8, marginLeft: compact ? 0 : 12, display: 'block' }}
      disabled={readonly}
    />
  </div>
);

const renderScaleQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => {
  const config = question.questionOptions?.config || {};
  const currentValue = formValues[question.id];
  
  // Custom marks mode
  if (config.useCustomMarks && config.customMarks?.length > 0) {
    const sortedMarks = [...config.customMarks].sort((a, b) => a.position - b.position);
    const marks = {};
    sortedMarks.forEach(m => { marks[m.position] = m.label; });
    const step = config.step || 1;
    const maxPos = config.maxPosition || 100;
    
    // Get value from position (interpolate numeric ranges)
    const getValue = (pos) => {
      const exact = sortedMarks.find(m => m.position === pos);
      if (exact) return exact.value;
      for (let i = 0; i < sortedMarks.length - 1; i++) {
        const lo = sortedMarks[i], hi = sortedMarks[i + 1];
        if (pos > lo.position && pos < hi.position) {
          const loNum = parseFloat(lo.value), hiNum = parseFloat(hi.value);
          if (!isNaN(loNum) && !isNaN(hiNum)) {
            const ratio = (pos - lo.position) / (hi.position - lo.position);
            return Math.round((loNum + (hiNum - loNum) * ratio) / step) * step;
          }
          return lo.value;
        }
      }
      return sortedMarks[0]?.value;
    };
    
    // Get position from value
    const getPos = (val) => {
      const exact = sortedMarks.find(m => String(m.value) === String(val));
      if (exact) return exact.position;
      const num = parseFloat(val);
      if (!isNaN(num)) {
        for (let i = 0; i < sortedMarks.length - 1; i++) {
          const lo = sortedMarks[i], hi = sortedMarks[i + 1];
          const loNum = parseFloat(lo.value), hiNum = parseFloat(hi.value);
          if (!isNaN(loNum) && !isNaN(hiNum) && num >= loNum && num <= hiNum) {
            return lo.position + (hi.position - lo.position) * ((num - loNum) / (hiNum - loNum));
          }
        }
      }
      return sortedMarks[0]?.position || 0;
    };
    
    const sliderValue = currentValue != null ? getPos(currentValue) : sortedMarks[0]?.position || 0;
    
    return (
      <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
        {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
        <div style={{ marginTop: 8, marginLeft: compact ? 0 : 12, maxWidth: 400 }}>
          <Slider
            min={0}
            max={maxPos}
            step={step}
            marks={marks}
            value={sliderValue}
            onChange={(pos) => handleInputChange(question.id, getValue(pos))}
            disabled={readonly}
            tooltip={{ formatter: (pos) => String(getValue(pos)) }}
          />
        </div>
      </div>
    );
  }
  
  // Numeric mode
  const min = config.min ?? 0;
  const max = config.max ?? 10;
  const step = config.step ?? 1;
  const marks = config.showMarks !== false ? {
    [min]: config.minLabel || min,
    [max]: config.maxLabel || max
  } : undefined;
  const numValue = typeof currentValue === 'number' ? currentValue : min;

  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{ marginTop: 8, marginLeft: compact ? 0 : 12, maxWidth: 400 }}>
        <Slider 
          min={min} 
          max={max} 
          step={step} 
          marks={marks} 
          value={numValue} 
          onChange={(v) => handleInputChange(question.id, v)} 
          disabled={readonly}
          tooltip={{ formatter: (val) => String(val) }}
        />
      </div>
    </div>
  );
};

const renderFileQuestion = (question, formValues, handleInputChange, readonly, compact, showLabel = true) => {
  const config = question.questionOptions?.config || {};
  return (
    <div key={question.id} style={{ marginBottom: compact ? 12 : 20 }}>
      {showLabel && <Text strong>{question.label}<RequiredMark question={question} /></Text>}
      <div style={{ marginTop: 8, marginLeft: compact ? 0 : 12 }}>
        <FileUploader
          value={formValues[question.id] || []}
          onChange={(files) => handleInputChange(question.id, files)}
          readonly={readonly}
          accept={config.accept}
          maxFiles={config.maxFiles}
          maxSizeMB={config.maxSizeMB}
          category={config.category}
        />
      </div>
    </div>
  );
};

const renderGroupQuestion = (question, renderQuestion, compact) => (
  <div key={question.id} style={{ marginBottom: compact ? 16 : 24 }}>
    <Title level={5}>{question.label}</Title>
    <div style={{ marginLeft: compact ? 8 : 16, marginTop: 8 }}>
      {question.questions?.map(renderQuestion)}
    </div>
  </div>
);

const renderEmbeddedSectionQuestion = (question, formValues, renderQuestion, compact) => {
  const sourceQuestionId = question.sourceQuestion;
  const selectedValue = formValues[sourceQuestionId];
  const sectionMapping = question.sectionMapping || {};
  const embeddedQuestions = sectionMapping[selectedValue];

  if (!embeddedQuestions || embeddedQuestions.length === 0) {
    return null;
  }

  return (
    <div key={question.id} style={{ 
      marginBottom: compact ? 16 : 24,
      padding: 16,
      border: '1px dashed #d9d9d9',
      borderRadius: 4,
      backgroundColor: '#fafafa'
    }}>
      {question.label && <Title level={5}>{question.label}</Title>}
      <div style={{ marginTop: 12 }}>
        {embeddedQuestions.map(renderQuestion)}
      </div>
    </div>
  );
};

const renderExternalFormQuestion = (question, formValues, handleInputChange, readonly, compact, externalForms, setExternalForms, onLoadExternalForm, renderExternalForm) => {
  const externalSourceQuestion = question.sourceQuestion;
  const externalSelectedValue = formValues[externalSourceQuestion];
  const formReferenceMapping = question.formReferenceMapping || {};
  const referencedFormId = formReferenceMapping[externalSelectedValue];
  
  if (!referencedFormId) {
    return null;
  }

  // Check if form is already loaded
  const externalFormSchema = externalForms[referencedFormId];
  
  if (!externalFormSchema) {
    // Load external form asynchronously
    if (onLoadExternalForm && !externalForms[`loading_${referencedFormId}`]) {
      setExternalForms(prev => ({ ...prev, [`loading_${referencedFormId}`]: true }));
      onLoadExternalForm(referencedFormId).then(schema => {
        setExternalForms(prev => ({
          ...prev,
          [referencedFormId]: schema,
          [`loading_${referencedFormId}`]: false
        }));
      }).catch(() => {
        setExternalForms(prev => ({
          ...prev,
          [`loading_${referencedFormId}`]: false
        }));
      });
    }
    return (
      <div key={question.id} style={{ padding: 16, textAlign: 'center' }}>
        <Spin tip="Loading external form..." />
      </div>
    );
  }

  return (
    <div key={question.id} style={{ 
      marginBottom: compact ? 16 : 24,
      padding: 16,
      border: '2px solid #1890ff',
      borderRadius: 4,
      backgroundColor: '#f0f7ff'
    }}>
      {question.label && (
        <Title level={5} style={{ color: '#1890ff', marginBottom: 16 }}>
          {question.label}
        </Title>
      )}
      {renderExternalForm && renderExternalForm(externalFormSchema, formValues[`${question.id}_data`], (values) => {
        handleInputChange(`${question.id}_data`, values);
      })}
    </div>
  );
};

